import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import PrintableQuote from '../printable/PrintableQuote'; // Fixed path
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useReactToPrint } from 'react-to-print';

// Define the expected structure of the fetched quote data
// (Should match the structure returned by getQuoteDataForPrint)
interface QuoteData {
  quoteNumber: string;
  quoteDate: Date | string;
  validUntil: Date | string;
  totalAmount: number;
  discountAmount: number;
  finalAmount: number;
  termsAndConditions?: string | null;
  notes?: string | null;
  client: {
    name: string;
    director?: string | null;
    phone?: string | null;
    email?: string | null;
    address?: string | null;
  };
  items: Array<{
    structure: {
      code: string;
      type: string;
      location: string;
    };
    startDate: Date | string;
    endDate: Date | string;
    unitPrice: number;
    quantity: number;
    discountPercentage: number;
    finalPrice: number;
    isMonthlyRate: boolean | null;
  }>;
}


const QuotePrintPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [quoteData, setQuoteData] = useState<QuoteData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const componentRef = useRef<HTMLDivElement>(null);

  const handlePrint = useReactToPrint({
    contentRef: componentRef, 
    documentTitle: `Коммерческое предложение № ${quoteData?.quoteNumber || id}`, 
    pageStyle: `@page { size: A4; margin: 20mm; }
                @media print {
                  body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
                }`,
    onAfterPrint: () => {
      console.log('Print dialog closed or printing started.');
    },
  });



  useEffect(() => {
    const fetchQuoteData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Use the new backend endpoint
        const response = await axios.get<QuoteData>(`/quotes/${id}/print-data`);
        setQuoteData(response.data);
        // Attempt to trigger print dialog after data is loaded
        // Use setTimeout to allow the component to render first
        // setTimeout(() => {
          // window.print();
        // }, 500); // Small delay
      } catch (err: any) {
        console.error('Error fetching quote data for printing:', err);
        setError(err.response?.data?.message || 'Не удалось загрузить данные для печати.');
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchQuoteData();
    } else {
      setError('ID коммерческого предложения не найден.');
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!loading && !error && quoteData && componentRef.current) {
      setTimeout(() => {
        handlePrint();
      }, 100);
    }
  }, [loading, error, quoteData, handlePrint]);


  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return <div className="printable-container error-message">Ошибка: {error}</div>;
  }

  if (!quoteData) {
    return <div className="printable-container">Данные для печати не найдены.</div>;
  }

  console.log('Quote data for printing:', quoteData);
  return (
    <div style={{ display: 'none' }}>
      <PrintableQuote quoteData={quoteData} ref={componentRef} />
    </div>
  );
};

export default QuotePrintPage;
